/**
 * Lightweight Mobile-Only Dot Pagination for Product Gallery
 */
if (!customElements.get('mobile-gallery-dots')) {
  customElements.define('mobile-gallery-dots', class extends HTMLElement {
    constructor() {
      super();
      this.mediaGallery = null;
      this.sliderComponent = null;
      this.isChanging = false;
    }
    
    connectedCallback() {
      // Only run on mobile
      if (window.innerWidth >= 750) return;
      
      // Find elements
      this.mediaGallery = document.querySelector('media-gallery');
      this.sliderComponent = this.mediaGallery?.querySelector('slider-component');
      if (!this.mediaGallery || !this.sliderComponent) return;
      
      // Setup events
      document.addEventListener('variant:changed', () => this.handleVariantChange());
      document.addEventListener('change', (e) => {
        if (e.target.closest('.product-form__input') && 
            !e.target.closest('.quantity__input') && 
            !e.target.closest('[name="quantity"]')) {
          this.handleVariantChange();
        }
      });
      
      // Use the native slideChanged event from Dawn
      this.sliderComponent.addEventListener('slideChanged', (e) => {
        if (!this.isChanging && e.detail?.currentElement?.dataset?.mediaId) {
          this.updateActiveDot(e.detail.currentElement.dataset.mediaId);
        }
      });
      
      // Create dots
      this.addDotPagination();
    }
    
    addDotPagination() {
      if (!this.mediaGallery || !this.sliderComponent) return;
      
      // Remove existing dots
      const existingDots = this.mediaGallery.querySelector('.gallery-dot-pagination');
      if (existingDots) existingDots.remove();
      
      // Get visible media items
      const mediaItems = Array.from(this.mediaGallery.querySelectorAll('.product__media-item'))
        .filter(item => !item.classList.contains('filtered-variant'));
      if (mediaItems.length <= 1) return;
      
      // Find active media
      const activeItem = mediaItems.find(item => item.classList.contains('is-active'));
      const activeId = activeItem ? activeItem.dataset.mediaId : mediaItems[0].dataset.mediaId;
      
      // Create dots container
      const pagination = document.createElement('div');
      pagination.className = 'gallery-dot-pagination';
      pagination.setAttribute('role', 'tablist');
      
      // Create dot container
      const dotContainer = document.createElement('div');
      dotContainer.className = 'dot-container';
      
      // Add click handler
      dotContainer.addEventListener('click', (e) => {
        const dot = e.target.closest('[data-action="select-media"]');
        if (!dot || this.isChanging) return;
        
        const mediaId = dot.dataset.target;
        this.mediaGallery?.setActiveMedia?.(mediaId, false);
      });
      
      // Add dots
      mediaItems.forEach((item, i) => {
        const mediaId = item.dataset.mediaId;
        const isActive = mediaId === activeId;
        
        const dot = document.createElement('button');
        dot.className = isActive ? 'dot is-active' : 'dot';
        dot.dataset.target = mediaId;
        dot.dataset.mediaPosition = i + 1;
        dot.dataset.action = 'select-media';
        dot.setAttribute('role', 'tab');
        dot.setAttribute('aria-selected', isActive);
        dot.setAttribute('aria-current', isActive);
        
        dotContainer.appendChild(dot);
      });
      
      // Add to DOM
      pagination.appendChild(dotContainer);
      this.sliderComponent.after(pagination);
    }
    
    updateActiveDot(mediaId) {
      if (!mediaId || !this.mediaGallery) return;
      
      const dots = this.mediaGallery.querySelectorAll('.gallery-dot-pagination .dot');
      for (const dot of dots) {
        const isActive = dot.dataset.target === mediaId;
        if (dot.classList.contains('is-active') !== isActive) {
          dot.classList.toggle('is-active', isActive);
          dot.setAttribute('aria-selected', isActive);
          dot.setAttribute('aria-current', isActive);
        }
      }
    }
    
    handleVariantChange() {
      if (this.isChanging || window.innerWidth >= 750 || !this.mediaGallery) return;
      this.isChanging = true;
      
      // Hide dots during variant change
      const dots = this.mediaGallery.querySelector('.gallery-dot-pagination');
      if (dots) dots.style.display = 'none';
      
      // Wait for variant filtering to complete
      setTimeout(() => {
        this.addDotPagination();
        
        // Show dots again
        const newDots = this.mediaGallery.querySelector('.gallery-dot-pagination');
        if (newDots) newDots.style.display = '';
        
        // Done changing
        this.isChanging = false;
      }, 350);
    }
  });
  
  // Initialize on DOMContentLoaded
  document.addEventListener('DOMContentLoaded', () => {
    if (!document.querySelector('mobile-gallery-dots')) {
      document.body.appendChild(document.createElement('mobile-gallery-dots'));
    }
  });
}